import React, { forwardRef } from 'react';
import styled, { CSSProp } from 'styled-components';
import {
  variant as styledVariant,
  compose,
  space,
  layout,
  color,
  typography,
  SpaceProps,
  LayoutProps,
  ColorProps,
  TypographyProps,
} from 'styled-system';
import css, { CssFunctionReturnType } from '@styled-system/css';

const borderWidth = '3px';
export type BtnSizes = 'sm' | 'md' | 'lg';
export type BtnVariants = 'primary' | 'secondary' | 'outline' | 'select' | 'circle';

type BaseButtonProps = {
  variant?: BtnVariants;
  btnSize?: BtnSizes;
  css?: CSSProp | CssFunctionReturnType;
  as?: React.ElementType;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  SpaceProps &
  LayoutProps &
  ColorProps &
  TypographyProps;

type ButtonAsAnchorProps = {
  as: 'a';
} & React.AnchorHTMLAttributes<HTMLAnchorElement> &
  BaseButtonProps;

type ButtonProps = BaseButtonProps | ButtonAsAnchorProps;

const StyledButton = styled.button<ButtonProps>(
  css({
    display: 'inline-block',
    borderWidth,
    borderStyle: 'solid',
    borderRadius: 'xs',
    appearance: 'none',
    cursor: 'pointer',
    textAlign: 'center',
    fontFamily: 'inherit',
    fontWeight: 'button',
    lineHeight: 'button',
    letterSpacing: '0.02em',
    userSelect: 'none',
    textDecoration: 'none',
    transition:
      'background 250ms ease-in, border-color 250ms ease-in-out, transform 150ms ease, box-shadow 300ms ease',
    '&:focus': {
      outline: 0,
      boxShadow: 'buttonFocus',
    },
    '&:disabled': {
      bg: 'gray20',
      borderColor: 'transparent',
      color: 'gray70',
      cursor: 'not-allowed',
      '&:hover': {
        bg: 'gray20',
      },
    },
  }),
  styledVariant({
    variants: {
      primary: {
        bg: 'blue100',
        borderColor: 'transparent',
        color: 'white',
        '&:hover': {
          bg: 'blueDark',
        },
      },
      secondary: {
        bg: 'teal50',
        borderColor: 'transparent',
        color: 'blue100',
        '&:hover': {
          bg: 'teal100',
        },
      },
      outline: {
        bg: 'transparent',
        borderColor: 'blue100',
        color: 'blue100',
        '&:hover': {
          borderColor: 'blueDark',
          color: 'blueDark',
        },
      },
      select: {
        bg: 'transparent',
        border: 'none',
        color: 'blue100',
        marginRight: '16px',
        '&:hover': {
          fontWeight: 'bold',
          backgroundColor: 'backgroundLightGray2',
        },
      },
      circle: {
        bg: 'transparent',
        border: 'none',
        borderWidth: '0px',
        borderRadius: 'circle',
        width: '48px',
        height: '48px',
        mx: '1px',
        '&:hover': {
          backgroundColor: 'backgroundLightGray2',
          fontWeight: 'bold',
        },
        '&:focus': {
          boxShadow: 'none',
        },
      },
    },
  }),
  styledVariant({
    prop: 'active',
    variants: {
      active: {
        fontWeight: 'bold',
        backgroundColor: 'backgroundLightGray2',
      },
    },
  }),
  ({ theme }) =>
    styledVariant({
      prop: 'btnSize',
      variants: {
        sm: {
          px: `calc(12px - ${borderWidth})`,
          py: `calc(9px - ${borderWidth})`,
          fontSize: 1,
        },
        md: {
          px: `calc(${theme.space[3]}px - ${borderWidth})`,
          py: `calc(12px - ${borderWidth})`,
          fontSize: 2,
        },
        lg: {
          px: `calc(${theme.space[5]}px - ${borderWidth})`,
          py: `calc(${theme.space[3]}px - ${borderWidth})`,
          fontSize: 2,
        },
      },
    }),
  compose(space, layout, color, typography)
);

/**
 * @deprecated
 */
export const btnVariants = ['primary', 'secondary', 'outline', 'select', 'circle'];

/**
 * @deprecated
 */
export const btnSizes = ['sm', 'md', 'lg'];

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', btnSize = 'md', ...props }, ref) => (
    <StyledButton
      variant={variant as BtnVariants}
      btnSize={btnSize as BtnSizes}
      ref={ref}
      {...props}
    />
  )
);

Button.displayName = 'Button';

export default Button;
